import "@/App.css";
import { lazy, Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SeoManager from "@/components/SeoManager";
import TikTokManager from "@/components/TikTokManager";
import ConsentBanner from "@/components/ConsentBanner";
import { RealtimeProvider } from "@/context/RealtimeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { setForbiddenHandler } from "@/lib/api";
import { AGE_ERROR, extractErrorCode, isRestricted, resolveLandingRoute } from "@/constants/ageContract";
import LandingPage from "@/pages/LandingPage";
import { hasRecordedConsent } from "@/lib/consent";
import DevDiagnostics from "@/components/DevDiagnostics";
import AiResumePrompt from "@/components/AiResumePrompt";
import { VerificationGateProvider, useVerificationGate } from "@/components/VerificationGate";
import { isFullVerificationError, getErrorMissing } from "@/lib/verification";

// VOYALYO-ROUTE-PERFORMANCE
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const DiscoverPage = lazy(() => import("@/pages/DiscoverPage"));
const TripsPage = lazy(() => import("@/pages/TripsPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const AppBookingSearchPage = lazy(() => import("@/pages/AppBookingSearchPage"));
const TripDetailPage = lazy(() => import("@/pages/TripDetailPage"));
const MatchesPage = lazy(() => import("@/pages/MatchesPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const PremiumPage = lazy(() => import("@/pages/PremiumPage"));
const TripEditorPage = lazy(() => import("@/pages/TripEditorPage"));
const NewTripChoicePage = lazy(() => import("@/pages/NewTripChoicePage"));
const AiTripPlannerPage = lazy(() => import("@/pages/AiTripPlannerPage"));
const LyoTripBuilderPage = lazy(() => import("@/pages/LyoTripBuilderPage"));
const UserProfilePage = lazy(() => import("@/pages/UserProfilePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ModerationPage = lazy(() => import("@/pages/ModerationPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminHubPage = lazy(() => import("@/pages/AdminHubPage"));
const AdminUserDetailPage = lazy(() => import("@/pages/AdminUserDetailPage"));
const AdminAgeVerificationsPage = lazy(() => import("@/pages/AdminAgeVerificationsPage"));
const AdminAgeVerificationDetailPage = lazy(() => import("@/pages/AdminAgeVerificationDetailPage"));
const AdminRegistrationsPage = lazy(() => import("@/pages/AdminRegistrationsPage"));
const AdminInspirationsPage = lazy(() => import("@/pages/AdminInspirationsPage"));
const AdminSocialDraftsPage = lazy(() => import("@/pages/AdminSocialDraftsPage"));
const AgeVerificationPage = lazy(() => import("@/pages/AgeVerificationPage"));
const EmailVerificationPage = lazy(() => import("@/pages/EmailVerificationPage"));
const SuspendedPage = lazy(() => import("@/pages/SuspendedPage"));
const GuidePage = lazy(() => import("@/pages/GuidePage"));
const ConsentGatePage = lazy(() => import("@/pages/ConsentGatePage"));
const LegalPage = lazy(() => import("@/pages/LegalPage").then(module => ({ default: module.LegalPage })));
const LegalHubPage = lazy(() => import("@/pages/LegalPage").then(module => ({ default: module.LegalHubPage })));
const LegalOverviewPage = lazy(() => import("@/pages/LegalPage").then(module => ({ default: module.LegalOverviewPage })));

function RouteLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-foreground" role="status" aria-live="polite">
      <p>Seite wird geladen …</p>
    </div>
  );
}

function Protected({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}
// Social-Routen: für eingeschränkte Minderjährige gesperrt (Redirect zur Statusseite).
function Social({ children }) {
  return <ProtectedRoute social>{children}</ProtectedRoute>;
}

// Verarbeitet die Rückkehr aus dem direkten Google-OAuth-Flow:
// liest das Einmal-Ticket und tauscht es gegen das Voyalyo-JWT.
function GoogleCallback() {
  const navigate = useNavigate();
  const { exchangeGoogleTicket } = useAuth();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get("ticket");

    // Ticket sofort aus der sichtbaren URL entfernen.
    window.history.replaceState(null, "", window.location.pathname);
    (async () => {
      if (!ticket) { navigate("/", { replace: true }); return; }
      try {
        const { user, onboardingComplete } = await exchangeGoogleTicket(ticket);
        // Social Login umgeht die rechtliche Bestätigung NICHT: liegt für die
        // aktuellen Versionsstände keine Zustimmung vor -> Consent-Gate anzeigen.
        if (!hasRecordedConsent(user)) { navigate("/rechtliche-bestaetigung", { replace: true }); return; }
        if (onboardingComplete === false) navigate("/complete-profile", { replace: true });
        else navigate(resolveLandingRoute(user), { replace: true });
      } catch (err) {
        const status = err?.response?.status;
        const msg = {
          400: "Google-Anmeldung fehlgeschlagen. Bitte versuche es erneut.",
          401: "Google-Sitzung ungültig oder abgelaufen. Bitte melde dich erneut an.",
          403: "Zugriff verweigert. Dieses Konto ist nicht berechtigt.",
          502: "Anmeldedienst vorübergehend nicht erreichbar. Bitte später erneut versuchen.",
        }[status] || "Google-Anmeldung fehlgeschlagen. Bitte versuche es erneut.";
        toast.error(msg);
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, exchangeGoogleTicket]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background" data-testid="google-callback">
      <p className="text-sm text-muted-foreground">Anmeldung wird abgeschlossen …</p>
    </div>
  );
}

// Age-1: globales 403-Handling. Sitzt INNERHALB des Routers, um navigieren zu können.
// Verhindert Redirect-Schleifen und Mehrfach-Hinweise (4s-Throttle).
function GlobalAgeGuard() {
  const navigate = useNavigate();
  const { openGate } = useVerificationGate();
  const lastToast = useRef(0);
  useEffect(() => {
    setForbiddenHandler((err) => {
      // Zentrale, wiederverwendbare UX für gesperrte aktive Kontaktfunktionen.
      // NUR der exakte Backend-Code FULL_VERIFICATION_REQUIRED löst dieses Modal aus.
      if (isFullVerificationError(err)) {
        openGate(getErrorMissing(err));
        return;
      }
      const code = extractErrorCode(err);
      if (!code) return;
      const now = Date.now();
      const throttled = now - lastToast.current < 4000;
      const notify = (fn) => { if (!throttled) { fn(); lastToast.current = now; } };
      switch (code) {
        case AGE_ERROR.VERIFICATION_REQUIRED:
          if (window.location.pathname !== "/altersverifikation") navigate("/altersverifikation");
          notify(() => toast.error("Für dein Konto ist eine Altersverifikation erforderlich."));
          break;
        case AGE_ERROR.GROUP_RESTRICTED:
          notify(() => toast.error("Profil oder Inhalt nicht verfügbar."));
          break;
        case AGE_ERROR.BIRTH_DATE_LOCKED:
          notify(() => toast.error("Dein bestätigtes Geburtsdatum kann nicht direkt geändert werden. Bitte wende dich an den Support."));
          break;
        case AGE_ERROR.TRANSITION_PENDING:
          notify(() => toast("Dein Altersstatus wird derzeit geprüft. Einige Funktionen sind vorübergehend eingeschränkt."));
          break;
        default:
          break;
      }
    });
    return () => setForbiddenHandler(null);
  }, [navigate, openGate]);
  return null;
}

function PublicHome() {
  const { user } = useAuth();
  return user ? <AuthPage initialView="login" /> : <LandingPage />;
}

function RoutedApp() {
  const location = useLocation();
  // Google-Callback zuerst erkennen (synchron, vor allen geschützten Routen).
  if (
    location.pathname === "/auth/google/callback" &&
    new URLSearchParams(location.search).has("ticket")
  ) return <GoogleCallback />;
  return (
    <VerificationGateProvider>
      <GlobalAgeGuard />
      <AiResumePrompt />
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/" element={<PublicHome />} />
        <Route path="/login" element={<AuthPage initialView="login" />} />
        <Route path="/registrieren" element={<AuthPage initialView="register" />} />
        <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
        <Route path="/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
        {/* Rechtliche Bestätigung nach Social Login */}
        <Route path="/rechtliche-bestaetigung" element={<ConsentGatePage />} />
        {/* Social routes – gesperrt für eingeschränkte Minderjährige */}
        <Route path="/entdecken" element={<Social><DiscoverPage /></Social>} />
        <Route path="/reisen" element={<Social><TripsPage /></Social>} />
<Route path="/reisen-buchen" element={<Protected><BookingPage /></Protected>} />
        <Route path="/reisen-buchen/:bookingType" element={<Protected><BookingPage /></Protected>} />
        <Route path="/reise/neu" element={<Protected><NewTripChoicePage /></Protected>} />
        <Route path="/reise/neu/manuell" element={<Social><TripEditorPage /></Social>} />
        <Route path="/reise/neu/ki" element={<Protected><AiTripPlannerPage /></Protected>} />
        <Route path="/reise/lyo" element={<Protected><LyoTripBuilderPage /></Protected>} />
        <Route path="/reise/:id/bearbeiten" element={<Social><TripEditorPage /></Social>} />
        <Route path="/reise/:id" element={<Social><TripDetailPage /></Social>} />
        <Route path="/matches" element={<Social><MatchesPage /></Social>} />
        <Route path="/nachrichten" element={<Social><ChatPage /></Social>} />
        <Route path="/nachrichten/:chatId" element={<Social><ChatPage /></Social>} />
        <Route path="/chat" element={<Social><ChatPage /></Social>} />
        <Route path="/chat/:chatId" element={<Social><ChatPage /></Social>} />
        <Route path="/nutzer/:id" element={<Social><UserProfilePage /></Social>} />
        {/* Immer erlaubt (auch für eingeschränkte Minderjährige) */}
        <Route path="/profil" element={<Protected><ProfilePage /></Protected>} />
        <Route path="/complete-profile" element={<Protected><ProfilePage initialEdit completeHint /></Protected>} />
        <Route path="/einstellungen" element={<Protected><SettingsPage /></Protected>} />
        {/* Lyo Guide – auch vor voller Verifizierung erreichbar (Restriction-Gates werden NICHT umgangen) */}
        <Route path="/guide" element={<Protected><GuidePage /></Protected>} />
        <Route path="/altersverifikation" element={<Protected><AgeVerificationPage /></Protected>} />
        <Route path="/e-mail-bestaetigen" element={<Protected><EmailVerificationPage /></Protected>} />
        <Route path="/premium" element={<Protected><PremiumPage /></Protected>} />
        <Route path="/benachrichtigungen" element={<Protected><NotificationsPage /></Protected>} />
        <Route path="/konto-gesperrt" element={<Protected><SuspendedPage /></Protected>} />
        {/* Legal/Support – öffentlich erreichbar */}
        <Route path="/hilfe-sicherheit" element={<LegalHubPage />} />
        <Route path="/rechtliches" element={<LegalOverviewPage />} />
        <Route path="/rechtliches/impressum" element={<LegalPage kind="impressum" />} />
        <Route path="/rechtliches/datenschutz" element={<LegalPage kind="datenschutz" />} />
        <Route path="/rechtliches/nutzungsbedingungen" element={<LegalPage kind="nutzungsbedingungen" />} />
        <Route path="/rechtliches/community-richtlinien" element={<LegalPage kind="community" />} />
        <Route path="/rechtliches/sicherheitsrichtlinien" element={<LegalPage kind="sicherheit" />} />
        <Route path="/rechtliches/premium-widerruf" element={<LegalPage kind="premium-widerruf" />} />
        <Route path="/rechtliches/konto-loeschen" element={<LegalPage kind="konto-loeschen" />} />
        <Route path="/child-safety" element={<LegalPage kind="child-safety" />} />
        <Route path="/rechtliches/support" element={<LegalPage kind="support" />} />
        <Route path="/impressum" element={<LegalPage kind="impressum" />} />
        <Route path="/datenschutz" element={<LegalPage kind="datenschutz" />} />
        <Route path="/nutzungsbedingungen" element={<LegalPage kind="nutzungsbedingungen" />} />
        <Route path="/community-richtlinien" element={<LegalPage kind="community" />} />
        <Route path="/jugendschutz" element={<LegalPage kind="jugendschutz" />} />
        <Route path="/sicherheit" element={<LegalPage kind="sicherheit" />} />
        <Route path="/premium-abo-widerruf" element={<LegalPage kind="premium-widerruf" />} />
        <Route path="/support" element={<LegalPage kind="support" />} />
        {/* Admin/Moderation – privilegiert */}
        <Route path="/moderation" element={<Protected><ModerationPage /></Protected>} />
        <Route path="/admin" element={<Protected><AdminHubPage /></Protected>} />
        <Route path="/admin/nutzer" element={<Protected><AdminDashboardPage /></Protected>} />
        <Route path="/admin/nutzer/:id" element={<Protected><AdminUserDetailPage /></Protected>} />
        <Route path="/admin/altersverifizierung" element={<Protected><AdminAgeVerificationsPage /></Protected>} />
        <Route path="/admin/altersverifizierung/:id" element={<Protected><AdminAgeVerificationDetailPage /></Protected>} />
        <Route path="/admin/registrierungen" element={<Protected><AdminRegistrationsPage /></Protected>} />
        <Route path="/admin/inspirationen" element={<Protected><AdminInspirationsPage /></Protected>} />
        <Route path="/admin/social-media" element={<Protected><AdminSocialDraftsPage /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      {process.env.NODE_ENV === "development" && <DevDiagnostics />}
    </VerificationGateProvider>
  );
}

function App() {
  // Öffentliche Suchmaske ohne Konto-/Social-Daten für die native WebView.
  const appBooking = window.location.pathname.match(/^\/app\/reisesuche\/(pauschalreisen|hotels)\/?$/);
  if (appBooking) {
    return <Suspense fallback={<RouteLoading />}><AppBookingSearchPage type={appBooking[1] === "hotels" ? "hotel" : "package"} /></Suspense>;
  }
  return (
    <AuthProvider>
      <BrowserRouter>
        <RealtimeProvider>
          <Toaster position="top-center" richColors />
          <SeoManager />
          <TikTokManager />
          <ConsentBanner />
          <RoutedApp />
        </RealtimeProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
